import type { S3File } from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@dune/ui";
import React, { useState } from "react";
import { toast } from "sonner";
import { registerS3File } from "../../api/queries/files.queries";
import DuneSignatorySelect from "./DuneSignatorySelect";
import { S3FileInput } from "./S3FileInput";

interface S3FileWithId extends S3File {
	id?: string;
}

interface TaskUploadDocumentModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onUpload: (fileId: string, signataireDuneId?: number) => Promise<void>;
	isPending?: boolean;
	requireSignatory?: boolean;
}

const TaskUploadDocumentModal: React.FC<TaskUploadDocumentModalProps> = ({
	open,
	onOpenChange,
	onUpload,
	isPending = false,
	requireSignatory = false,
}) => {
	const [selectedFile, setSelectedFile] = useState<S3FileWithId | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [selectedSignatoryId, setSelectedSignatoryId] = useState<string>("");

	const handleFileUpload = async (file: S3File) => {
		setSelectedFile(file);

		try {
			setIsUploading(true);

			const registeredFile = await registerS3File({
				bucket: file.bucket,
				key: file.key,
				name: file.name,
				mimeType: file.type,
			});

			setSelectedFile({
				...file,
				id: registeredFile.id,
			});
		} catch (error) {
			console.error("File registration failed:", error);
			toast.error("Erreur lors de l'enregistrement du fichier");
			setSelectedFile(null);
		} finally {
			setIsUploading(false);
		}
	};

	const handleUpload = async () => {
		if (!selectedFile || !selectedFile.id) {
			toast.error("Veuillez sélectionner un fichier");
			return;
		}

		if (requireSignatory && !selectedSignatoryId) {
			toast.error("Veuillez sélectionner un signataire Dune");
			return;
		}

		try {
			await onUpload(
				selectedFile.id,
				requireSignatory ? Number(selectedSignatoryId) : undefined,
			);

			toast.success("Document ajouté à la tâche avec succès");
			onOpenChange(false);
			setSelectedFile(null);
			setSelectedSignatoryId("");
		} catch (error) {
			console.error("Upload failed:", error);
			toast.error("Erreur lors de l'ajout du document");
		}
	};

	const handleModalClose = (newOpen: boolean) => {
		if (!newOpen) {
			setSelectedFile(null);
			setSelectedSignatoryId("");
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleModalClose}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle>Ajouter un document à la tâche</DialogTitle>
					<DialogDescription>
						Sélectionnez un fichier à associer à cette tâche
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div>
						<div className="text-sm font-medium mb-2">Fichier</div>
						<S3FileInput
							key={`task-upload-modal-${open}`}
							bucket="private"
							onUploadSuccess={handleFileUpload}
							hidePreviewButton={true}
							subText="Formats acceptés : PDF, DOCX, PNG, JPG (max. 10MB)"
						/>
					</div>

					{requireSignatory && (
						<DuneSignatorySelect
							value={selectedSignatoryId}
							onValueChange={setSelectedSignatoryId}
							id="task-upload-signatory-select"
							defaultToCurrentUser
						/>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Annuler
					</Button>
					<Button
						onClick={handleUpload}
						disabled={
							!selectedFile ||
							isPending ||
							isUploading ||
							!selectedFile.id ||
							(requireSignatory && !selectedSignatoryId)
						}
						isLoading={isPending || isUploading}
					>
						Ajouter le document
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default TaskUploadDocumentModal;
